import {
  createMachine,
  assign,
  fromPromise,
  fromCallback,
  sendParent,
  stopChild,
  sendTo,
  setup,
} from "xstate";
import type {
  UploadMachineDefinition,
  FileUploadInput,
  FileUploadActor,
  FileUploadMachineDefinition,
  FilePartUploadDefinition,
  FilePartUploadInput,
  UploadMachineInput,
  UploadResponse,
} from "./types";
import {
  completeMultipartUpload,
  createMultipartUpload,
  presignPartUpload,
  uploadFilePartCallback,
  cancelUpload,
  getUploads,
  createUploads,
  deleteUploads,
} from "./services";

const PART_SIZE = 5 * 1024 * 1024;

export function generateUniqueFileIdentifier(
  file: File | { name: string; size: number; lastModified: number }
) {
  const lastDotIndex = file.name.lastIndexOf(".");
  const name =
    lastDotIndex > 0 ? file.name.substring(0, lastDotIndex) : file.name;
  const extension =
    lastDotIndex > 0 ? file.name.substring(lastDotIndex + 1).toLowerCase() : "";
  const id = `${name}-${file.size}-${file.lastModified}`;

  return {
    id,
    name: extension ? `${id}.${extension}` : id,
    extension,
  };
}

const partUploadMachine = createMachine({
  types: {} as FilePartUploadDefinition,
  id: "partUploadMachine",
  context: ({ input }: { input: FilePartUploadInput }) => ({
    key: input.key,
    uploadId: input.uploadId,
    number: input.partNumber,
    data: input.data,
    url: null,
    retries: 3,
    surveyId: input.surveyId,
    orderId: input.orderId,
  }),
  initial: "signing",
  states: {
    signing: {
      invoke: {
        id: "signing",
        src: fromPromise(presignPartUpload),
        input: ({ context }) => ({
          key: context.key,
          uploadId: context.uploadId,
          partNumber: context.number,
          surveyId: context.surveyId,
          orderId: context.orderId,
        }),
        onDone: {
          target: "uploading",
          actions: assign({
            url: ({ event }) => event.output.presignedUrl,
          }),
        },
      },
    },
    uploading: {
      invoke: {
        id: "uploadPart",
        src: fromCallback(uploadFilePartCallback),
        input: ({ context }) => ({
          number: context.number,
          url: context.url,
          data: context.data,
        }),
      },
      on: {
        ABORT: {
          actions: sendTo("uploadPart", {
            type: "REMOVE",
          }),
        },
        "uploadFilePartCallback.progress": {
          actions: sendParent(({ event, context }) => ({
            type: "partUploadMachine.progress",
            progress: event.progress,
            partNumber: event.number,
          })),
        },
        "uploadFilePartCallback.complete": {
          target: "success",
          actions: sendParent(({ event }) => ({
            type: "partUploadMachine.done",
            etag: event.etag,
            partNumber: event.number,
          })),
        },
        "uploadFilePartCallback.error": [
          {
            target: "uploading",
            actions: assign({
              retries: ({ context }) => context.retries - 1,
            }),
            guard: ({ context }) => context.retries > 0,
          },
          {
            target: "failure",
            actions: sendParent({ type: "partUploadMachine.error" }),
          },
        ],
        "uploadFilePartCallback.abort": {
          actions: sendParent(({ context }) => ({
            type: "partUploadMachine.abort",
            partNumber: context.number,
          })),
        },
      },
    },
    success: {
      type: "final",
    },
    failure: { type: "final" },
  },
});

const fileUploadMachine = createMachine(
  {
    types: {} as FileUploadMachineDefinition,
    id: "fileUploadMachine",
    context: ({ input }: { input: FileUploadInput }) => {
      const parts = [];

      for (let i = 0; i < Math.ceil(input.data.size / PART_SIZE); i++) {
        const start = i * PART_SIZE;
        const end = Math.min(start + PART_SIZE, input.data.size);

        parts.push({
          number: i + 1,
          start,
          end,
        });
      }

      return {
        attachmentId: null,
        key: input.key,
        file: input.data,
        uploadId: null,
        progress: 0,
        parts,
        lastPartNumber: 0,
        partProgress: {},
        activeParts: [],
        completeParts: [],
        partUploadActors: new Map(),
        error: null,
        surveyId: input.surveyId,
        orderId: input.orderId,
        maxConcurrentParts: input.maxConcurrentParts,
        startedAt: Date.now(),
        startingBytes: 0,
        speed: 0,
      };
    },
    initial: "create",
    states: {
      create: {
        invoke: {
          id: "create",
          src: fromPromise(createMultipartUpload),
          input: ({ context }) => ({
            key: context.key,
            fileName: context.file.name,
            fileSize: context.file.size,
            fileType: context.file.type,
            fileModifiedAt: context.file.lastModified,
            fileExtension: (() => {
              const lastDotIndex = context.file.name.lastIndexOf(".");
              return lastDotIndex > 0
                ? context.file.name.substring(lastDotIndex + 1).toLowerCase()
                : "";
            })(),
            surveyId: context.surveyId,
            orderId: context.orderId,
          }),
          onDone: [
            {
              target: "#fileUploadMachine.success",
              guard: ({ event }) => {
                if (event.output.status === "uploaded") {
                  return true;
                }

                return false;
              },
            },
            {
              target: "uploading",
              actions: assign(({ context, event }) => {
                const completedPartNumbers = event.output.completeParts
                  .map((part: { PartNumber: number }) => part.PartNumber)
                  .sort((a: number, b: number) => a - b);

                const lastPartNumber = completedPartNumbers.reduce(
                  (a: number, b: number) => Math.max(a, b),
                  0
                );

                const missingPartNumbers = Array.from(
                  { length: lastPartNumber },
                  (_, i) => i + 1
                )
                  .filter(
                    (partNumber) => !completedPartNumbers.includes(partNumber)
                  )
                  .sort((a: number, b: number) => a - b);

                const maxConcurrentParts = context.maxConcurrentParts;
                const activeParts =
                  missingPartNumbers.length > 0
                    ? missingPartNumbers
                    : lastPartNumber === context.parts.length
                    ? []
                    : Array.from(
                        { length: maxConcurrentParts },
                        (_, i) => i + lastPartNumber + 1
                      );

                let completedBytes = 0;
                for (const { PartNumber } of event.output.completeParts) {
                  const part = context.parts.find(
                    (p) => p.number === PartNumber
                  );
                  if (part) {
                    completedBytes += part.end - part.start;
                  }
                }

                return {
                  lastPartNumber: activeParts[activeParts.length - 1],
                  attachmentId: event.output.id,
                  uploadId: event.output.upload_reference,
                  completeParts: event.output.completeParts,
                  activeParts,
                  startedAt: Date.now(),
                  startingBytes: completedBytes,
                };
              }),
            },
          ],
        },
      },
      uploading: {
        entry: "spawnPartUploadMachine",
        initial: "checking",
        states: {
          active: {},
          checking: {
            always: [
              {
                target: "#fileUploadMachine.complete",
                guard: ({ context }) => {
                  return context.completeParts.length === context.parts.length;
                },
              },
              {
                target: "active",
              },
            ],
          },
        },
        on: {
          REMOVE: {
            target: "canceling",
          },
          "partUploadMachine.progress": {
            actions: [
              assign(({ context, event }) => {
                const completedNumbers = new Set(
                  context.completeParts.map((p) => p.PartNumber)
                );

                const partProgress =
                  completedNumbers.has(event.partNumber) === false
                    ? {
                        ...context.partProgress,
                        [event.partNumber]: Math.max(
                          0,
                          Math.min(1, event.progress)
                        ),
                      }
                    : { ...context.partProgress };

                const totalBytes = context.file.size || 1;

                let completedBytes = 0;
                for (const { PartNumber } of context.completeParts) {
                  const part = context.parts.find(
                    (p) => p.number === PartNumber
                  );
                  if (part) {
                    completedBytes += part.end - part.start;
                  }
                }

                let activeBytesProgress = 0;
                for (const partNumber of Object.keys(partProgress).map(
                  Number
                )) {
                  if (completedNumbers.has(partNumber)) continue;
                  const part = context.parts.find(
                    (p) => p.number === partNumber
                  );
                  if (part) {
                    const size = part.end - part.start;
                    activeBytesProgress +=
                      (partProgress[partNumber] || 0) * size;
                  }
                }

                const progress = Math.min(
                  1,
                  (completedBytes + activeBytesProgress) / totalBytes
                );

                const secondsSince = (Date.now() - context.startedAt) / 1000;

                return {
                  partProgress,
                  progress,
                  startedAt: secondsSince > 4 ? Date.now() : context.startedAt,
                  startingBytes:
                    secondsSince > 4
                      ? completedBytes + activeBytesProgress
                      : context.startingBytes,
                  speed:
                    (completedBytes +
                      activeBytesProgress -
                      context.startingBytes) /
                    secondsSince,
                };
              }),
              sendParent(({ context }) => ({
                type: "PROGRESS_UPLOAD",
              })),
            ],
          },
          "partUploadMachine.done": {
            target: "uploading.checking",
            actions: [
              ({ context, event }) => {
                const actorId = context.partUploadActors.get(event.partNumber);

                if (actorId) {
                  stopChild(actorId);
                }
              },
              assign(({ context, event, spawn }) => {
                const activeParts = context.activeParts.filter(
                  (part) => part !== event.partNumber
                );

                const { [event.partNumber]: _, ...partProgress } =
                  context.partProgress;

                const totalParts = context.parts.length;

                const partUploadActors = new Map(context.partUploadActors);
                partUploadActors.delete(event.partNumber);

                const maxConcurrentParts = context.maxConcurrentParts;
                const availableSlots = maxConcurrentParts - activeParts.length;

                const addParts = [];

                for (let i = 0; i < availableSlots; i++) {
                  if (
                    context.completeParts.length +
                      activeParts.length +
                      availableSlots ===
                    totalParts
                  ) {
                    break;
                  }

                  const partNumber = context.lastPartNumber + i + 1;

                  const part = context.parts.find(
                    (part) => part.number === partNumber
                  );

                  addParts.push(partNumber);

                  if (part && context.uploadId) {
                    const actorId = `part-${context.key}-${partNumber}`;

                    spawn(partUploadMachine, {
                      id: actorId,
                      input: {
                        key: context.key,
                        uploadId: context.uploadId,
                        partNumber,
                        data: context.file.slice(part.start, part.end),
                        surveyId: context.surveyId,
                        orderId: context.orderId,
                      },
                    });

                    partUploadActors.set(partNumber, actorId);
                  }
                }

                return {
                  partProgress,
                  lastPartNumber: context.lastPartNumber + availableSlots,
                  activeParts: activeParts.concat(addParts),
                  partUploadActors,
                  completeParts: [
                    ...context.completeParts,
                    {
                      ETag: event.etag,
                      PartNumber: event.partNumber,
                    },
                  ].sort((a, b) => a.PartNumber - b.PartNumber),
                };
              }),
            ],
          },
          "partUploadMachine.error": {
            target: "failure",
          },
        },
      },
      canceling: {
        always: {
          actions: [
            sendTo(
              ({ context }) => {
                const values = Array.from(
                  context.partUploadActors.values()
                ).sort((a, b) => (a === b ? 0 : a < b ? -1 : 1));

                return values[0];
              },
              {
                type: "ABORT",
              }
            ),
          ],
        },
        on: {
          "partUploadMachine.abort": {
            actions: assign(({ context, event }) => {
              const partUploadActors = new Map(context.partUploadActors);
              const actor = partUploadActors.get(event.partNumber);

              if (actor) {
                stopChild(actor);
              }

              partUploadActors.delete(event.partNumber);

              return {
                partUploadActors,
              };
            }),
            target: "check",
          },
        },
      },
      check: {
        always: [
          {
            target: "canceling",
            guard: ({ context }) => context.partUploadActors.size > 0,
          },
          { target: "cancel" },
        ],
      },
      cancel: {
        invoke: {
          id: "cancelUpload",
          src: fromPromise(cancelUpload),
          input: ({ context }) => ({
            attachmentId: context.attachmentId,
          }),
          onDone: {
            target: "removed",
          },
        },
      },
      complete: {
        entry: assign({
          progress: 1,
        }),
        invoke: {
          id: "complete",
          src: fromPromise(completeMultipartUpload),
          input: ({ context }) => ({
            key: context.key,
            uploadId: context.uploadId,
            parts: context.completeParts,
            surveyId: context.surveyId,
            orderId: context.orderId,
          }),
          onDone: {
            target: "success",
          },
        },
      },
      success: {
        entry: [
          assign({
            progress: 1,
          }),
          sendParent(({ context }) => {
            return {
              type: "COMPLETE_UPLOAD",
              key: context.key,
            };
          }),
        ],
      },
      removed: {
        type: "final",
        entry: [
          ({ context }) => {
            context.partUploadActors.forEach((actorId) => stopChild(actorId));
          },
          sendParent(({ context }) => {
            return {
              type: "CANCEL_UPLOAD",
              key: context.key,
            };
          }),
        ],
      },
      failure: {
        type: "final",
        entry: ({ context }) => {
          context.partUploadActors.forEach((actorId) => stopChild(actorId));
        },
      },
    },
  },
  {
    actions: {
      spawnPartUploadMachine: assign(({ context, spawn }) => {
        if (context.uploadId) {
          const partUploadActors = new Map(context.partUploadActors);

          for (const partNumber of context.activeParts) {
            const part = context.parts.find(
              (part) => partNumber === part.number
            );

            if (part) {
              const actorId = `part-${context.key}-${part.number}`;

              spawn(partUploadMachine, {
                id: actorId,
                input: {
                  key: context.key,
                  uploadId: context.uploadId,
                  partNumber: part.number,
                  data: context.file.slice(part.start, part.end),
                  surveyId: context.surveyId,
                  orderId: context.orderId,
                },
              });

              partUploadActors.set(part.number, actorId);
            }
          }

          return {
            partUploadActors,
          };
        }

        return context;
      }),
    },
  }
);

export type FileUploadMachine = typeof fileUploadMachine;

export const uploadMachine = setup({
  actors: {
    getUploads: fromPromise(getUploads),
    createUploads: fromPromise(createUploads),
    deleteUploads: fromPromise(deleteUploads),
    fileUploadMachine: fileUploadMachine,
  },
}).createMachine({
  types: {} as UploadMachineDefinition,
  id: "uploadMachine",
  context: ({ input }: { input?: UploadMachineInput }) => ({
    createdFiles: [] as string[],
    uploadedFiles: [] as string[],
    files: [] as FileUploadInput[],
    queuedFiles: [] as FileUploadInput[],
    fileUploadActors: [] as FileUploadActor[],
    maxConcurrentFiles: 6,
    maxConcurrentParts: 1,
    surveyId: input?.surveyId ?? 0,
    orderId: input?.orderId ?? null,
    imageUploads: [] as UploadResponse[],
    lidarUploads: [] as UploadResponse[],
    geometryUploads: [] as UploadResponse[],
    uploadedBytes: 0,
    totalBytes: 0,
    activeBytes: 0,
    resume: input?.resume ?? false,
  }),
  initial: "uploader",
  states: {
    uploader: {
      initial: "loading",
      states: {
        loading: {
          invoke: {
            id: "getUploads",
            src: "getUploads",
            input: ({ context }) => ({
              surveyId: context.surveyId,
              orderId: context.orderId,
            }),
            onDone: {
              actions: assign({
                createdFiles: ({ event }) =>
                  event.output
                    .filter((upload) => upload.status === "created")
                    .map((upload) => upload.key),
                uploadedFiles: ({ event }) => {
                  return event.output
                    .filter((upload) => upload.status === "uploaded")
                    .map((upload) => upload.key);
                },
                totalBytes: ({ event }) =>
                  event.output
                    .filter((upload) => upload.status === "uploaded")
                    .reduce((acc, upload) => acc + upload.file_size, 0),
                uploadedBytes: ({ event }) =>
                  event.output
                    .filter((upload) => upload.status === "uploaded")
                    .reduce((acc, upload) => acc + upload.file_size, 0),
                imageUploads: ({ event }) =>
                  event.output
                    .filter((upload) => {
                      const ext = upload.file_extension?.toLowerCase();
                      return ext === "jpg" || ext === "jpeg" || ext === "rjpeg";
                    })
                    .sort((a, b) => {
                      if (a.file_name < b.file_name) return -1;
                      if (a.file_name > b.file_name) return 1;
                      return 0;
                    }),
                lidarUploads: ({ event }) =>
                  event.output
                    .filter((upload) => {
                      const ext = upload.file_extension?.toLowerCase();
                      return ext === "las" || ext === "laz";
                    })
                    .sort((a, b) => {
                      if (a.file_name < b.file_name) return -1;
                      if (a.file_name > b.file_name) return 1;
                      return 0;
                    }),
                geometryUploads: ({ event }) =>
                  event.output
                    .filter((upload) => {
                      const ext = upload.file_extension?.toLowerCase();
                      return (
                        ext === "kml" ||
                        ext === "kmz" ||
                        ext === "geojson" ||
                        ext === "shp" ||
                        ext === "dbf" ||
                        ext === "prj" ||
                        ext === "shx" ||
                        ext === "zip" ||
                        ext === "csv" ||
                        ext === "pdf" ||
                        ext === "xlsx"
                      );
                    })
                    .sort((a, b) => {
                      if (a.file_name < b.file_name) return -1;
                      if (a.file_name > b.file_name) return 1;
                      return 0;
                    }),
              }),
              target: "idle",
            },
          },
        },
        idle: {
          on: {
            START_UPLOAD: {
              target: "uploading",
              actions: assign(({ context, event, spawn }) => {
                const { totalBytes, uploadedBytes } = event.files.reduce(
                  (
                    acc: { totalBytes: number; uploadedBytes: number },
                    file: FileUploadInput
                  ) => ({
                    totalBytes: acc.totalBytes + file.data.size,
                    uploadedBytes:
                      acc.uploadedBytes +
                      (context.uploadedFiles.includes(file.key)
                        ? file.data.size
                        : 0),
                  }),
                  {
                    totalBytes: context.totalBytes ?? 0,
                    uploadedBytes: context.uploadedBytes ?? 0,
                  }
                );

                const pendingFiles = event.files.filter(
                  (file: FileUploadInput) =>
                    context.uploadedFiles.includes(file.key) === false
                );

                const filesToUpload = pendingFiles.slice(
                  0,
                  context.maxConcurrentFiles
                );

                const queuedFiles = pendingFiles.slice(
                  context.maxConcurrentFiles,
                  pendingFiles.length
                );

                const fileUploadActors = filesToUpload.map(
                  (file: FileUploadInput) => {
                    const fileWithParts = {
                      ...file,
                      maxConcurrentParts: context.maxConcurrentParts,
                    };
                    const actor = spawn("fileUploadMachine", {
                      id: `file-${file.key}`,
                      input: fileWithParts,
                    });

                    return actor;
                  }
                );

                return {
                  ...context,
                  files: filesToUpload,
                  fileUploadActors,
                  queuedFiles,
                  totalBytes,
                  uploadedBytes,
                  resume: true,
                };
              }),
            },
            SUBMIT_ORDER: {
              actions: assign(({ context }) => ({
                resume: true,
              })),
            },
          },
        },
        uploading: {
          on: {
            COMPLETE_UPLOAD: {
              actions: [
                assign(({ context, event }) => {
                  const file = context.files.find(
                    (file: FileUploadInput) => file.key === event.key
                  );

                  if (file) {
                    return {
                      uploadedBytes:
                        context.uploadedBytes + (file.data.size ?? 0),
                    };
                  }

                  return {
                    uploadedBytes: context.uploadedBytes,
                  };
                }),
                ({ context, event }) => {
                  const actorToStop = context.fileUploadActors.find(
                    (actor: FileUploadActor) => actor.id === `file-${event.key}`
                  );

                  if (actorToStop) {
                    stopChild(actorToStop.id);
                  }
                },
                assign(({ context, event, spawn }) => {
                  const updatedActors = context.fileUploadActors.filter(
                    (actor: FileUploadActor) => actor.id !== `file-${event.key}`
                  );

                  const availableSlots =
                    context.maxConcurrentFiles - updatedActors.length;
                  const filesToStart = context.queuedFiles.slice(
                    0,
                    availableSlots
                  );
                  const remainingQueuedFiles =
                    context.queuedFiles.slice(availableSlots);

                  const newFileUploadActors = filesToStart.map(
                    (file: FileUploadInput) => {
                      const fileWithParts = {
                        ...file,
                        maxConcurrentParts: context.maxConcurrentParts,
                      };
                      const actor = spawn("fileUploadMachine", {
                        id: `file-${file.key}`,
                        input: fileWithParts,
                      });

                      return actor;
                    }
                  );

                  const fileUploadActors =
                    updatedActors.concat(newFileUploadActors);

                  return {
                    files: context.files
                      .filter((file: FileUploadInput) => file.key !== event.key)
                      .concat(filesToStart),
                    fileUploadActors,
                    queuedFiles: remainingQueuedFiles,
                    uploadedFiles: context.uploadedFiles.concat(event.key),
                    activeBytes: fileUploadActors.reduce(
                      (acc: number, actor: FileUploadActor) =>
                        acc +
                        actor.getSnapshot().context.progress *
                          actor.getSnapshot().context.file.size,
                      0
                    ),
                  };
                }),
              ],
            },
            PROGRESS_UPLOAD: {
              actions: assign(({ context, event }) => {
                return {
                  activeBytes: context.fileUploadActors.reduce(
                    (acc: number, actor: FileUploadActor) =>
                      acc +
                      actor.getSnapshot().context.progress *
                        actor.getSnapshot().context.file.size,
                    0
                  ),
                };
              }),
            },
            CANCEL_UPLOAD: [
              {
                target: "idle",
                actions: [
                  assign(({ context, event }) => {
                    const actorToStop = context.fileUploadActors.find(
                      (actor: FileUploadActor) =>
                        actor.id === `file-${event.key}`
                    );

                    if (actorToStop) {
                      stopChild(actorToStop.id);
                    }

                    return {
                      fileUploadActors: [] as FileUploadActor[],
                      queuedFiles: [] as FileUploadInput[],
                    };
                  }),
                ],
                guard: ({ context }) => context.queuedFiles.length === 0,
              },
              {
                actions: [
                  ({ context, event }) => {
                    const actorToStop = context.fileUploadActors.find(
                      (actor: FileUploadActor) =>
                        actor.id === `file-${event.key}`
                    );

                    if (actorToStop) {
                      stopChild(actorToStop.id);
                    }
                  },
                  assign(({ context, event, spawn }) => {
                    const updatedActors = context.fileUploadActors.filter(
                      (actor: FileUploadActor) =>
                        actor.id !== `file-${event.key}`
                    );

                    const availableSlots =
                      context.maxConcurrentFiles - updatedActors.length;
                    const filesToStart = context.queuedFiles.slice(
                      0,
                      availableSlots
                    );
                    const remainingQueuedFiles =
                      context.queuedFiles.slice(availableSlots);

                    const newFileUploadActors = filesToStart.map(
                      (file: FileUploadInput) => {
                        const fileWithParts = {
                          ...file,
                          maxConcurrentParts: context.maxConcurrentParts,
                        };
                        const actor = spawn("fileUploadMachine", {
                          id: `file-${file.key}`,
                          input: fileWithParts,
                        });

                        return actor;
                      }
                    );

                    return {
                      files: context.files
                        .filter(
                          (file: FileUploadInput) => file.key !== event.key
                        )
                        .concat(filesToStart),
                      fileUploadActors:
                        updatedActors.concat(newFileUploadActors),
                      queuedFiles: remainingQueuedFiles,
                    };
                  }),
                ],
              },
            ],
            CANCEL_ALL: {
              actions: assign(({ context, event }) => {
                for (const actor of context.fileUploadActors) {
                  actor.send({ type: "REMOVE" });
                }

                return {
                  queuedFiles: [] as FileUploadInput[],
                };
              }),
            },
            BACK: {
              actions: assign(({ context }) => ({
                resume: false,
              })),
            },
          },
        },
      },
    },
  },
});

export type UploadMachine = typeof uploadMachine;
